// @oasis-permission-inherits: /videos (mirage-videos:view)
import { useOnChangeEffect } from "@mirage-web/utils/react-helpers";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { InfiniteQueryPagination } from "~/components/infinite-query-pagination";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { DpoSegmentFlagDialog } from "~/modules/mirage/components/dpo-segment-flag-dialog";
import { DpoVideoInfoCard } from "~/modules/mirage/components/dpo-video-info-card";
import ResponsiveGrid from "~/modules/mirage/components/responsive-grid";
import { toggleVideoBookmarkMutation } from "~/modules/mirage/mutations";
import { fetchMirageVideosQuery } from "~/modules/mirage/queries";
import { useDpoFlagging } from "~/modules/mirage/use-dpo-flagging";
import { VideoCard } from "~/modules/videos/components/video-card";
import { excludeEmployeesStorage, hideProductInfoStorage } from "~/modules/videos/storage";

export const Route = createFileRoute("/_dash/videos/")({
  validateSearch: z.object({
    type: z.enum(["user", "org"]).optional().default("user"),
    id: z.string().optional(),
    page: z.number().optional(),
    caller: z.string().optional(),
    twins: z.boolean().optional(),
    reference: z.enum(["image", "video"]).optional(),
  }),
  component: RouteComponent,
});

const CALLER_OPTIONS = [
  { value: "all", label: "All Callers" },
  { value: "ai-edit", label: "AI Edit" },
  { value: "coeditor", label: "Co-Editor" },
  { value: "mirage-studio", label: "Mirage Studio" },
  { value: "paidapi", label: "Paid API" },
  { value: "api", label: "API" },
];

const REFERENCE_OPTIONS = [
  { value: "all", label: "All References" },
  { value: "video", label: "Reference Video" },
  { value: "image", label: "Reference Image" },
] as const;

type ReferenceMediaType = "image" | "video";

function RouteComponent() {
  const { type, id, page, caller, twins, reference } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const [filterType, setFilterType] = useState<"user" | "org">(() => type ?? "user");
  const [filterId, setFilterId] = useState<string>(() => id ?? "");

  const [excludeEmployeesState, setExcludeEmployeesState] = useState<boolean>(() => {
    const stored = excludeEmployeesStorage.get();
    return stored ?? true;
  });

  const [hideProductInfoState, setHideProductInfoState] = useState<boolean>(() => {
    const stored = hideProductInfoStorage.get();
    return stored ?? false;
  });

  const filterIdInputRef = useRef<HTMLInputElement>(null);

  useOnChangeEffect(type, (v) => {
    setFilterType(v as "user" | "org");
  });

  useOnChangeEffect(id, (v) => {
    setFilterId(v ?? "");
  });

  const onTypeChange = async (v: string) => {
    await navigate({
      search: {
        type: v as "user" | "org",
        id: "",
      },
    });
    setTimeout(() => {
      // hack to let menu close before focusing
      if (filterIdInputRef.current) {
        filterIdInputRef.current.focus();
      }
    }, 100);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const type = formData.get("type") as "user" | "org";
    const id = formData.get("id") as string;
    navigate({
      search: {
        type,
        id,
        caller: caller || undefined,
        twins,
        reference,
      },
    });
    setCurrentPageIndex(0);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    if (pastedText) {
      navigate({
        search: {
          type,
          id: pastedText,
        },
      });
      setCurrentPageIndex(0);
    }
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, refetch } =
    fetchMirageVideosQuery.useInfiniteQuery({
      params: {
        filters: {
          ...(id && type === "org" ? { orgId: id } : {}),
          ...(id && type === "user" ? { userId: id } : {}),
          excludeEmployees: excludeEmployeesState,
          ...(caller ? { caller } : {}),
          ...(twins ? { twinsOnly: true } : {}),
          ...(reference ? { referenceMediaType: reference } : {}),
        },
      },
    });

  const toggleBookmark = toggleVideoBookmarkMutation.useMutation({
    onSuccess: (data) => {
      toast.success(data.bookmarked ? "Video bookmarked" : "Bookmark removed");
      // Refetch to update the list
      refetch();
    },
    onError: () => {
      toast.error("Failed to update bookmark");
    },
  });

  const handleToggleBookmark = (videoId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleBookmark.mutate({
      pathParams: { videoId },
      params: {},
    });
  };

  const dpoFlagging = useDpoFlagging();
  const [dpoFlagVideoId, setDpoFlagVideoId] = useState<string | null>(null);

  const handleFlagForDpo = (videoId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDpoFlagVideoId(videoId);
  };

  const totalPages = data?.pages.length ?? 0;
  const [currentPageIndex, setCurrentPageIndex] = useState(
    page && page <= totalPages ? page - 1 : 0
  );

  const currentPageData = data?.pages[currentPageIndex];

  const searchParams = {
    type,
    id,
    caller: caller || undefined,
    twins,
    reference,
  };

  const onPreviousPage = () => {
    const previousPageIndex = Math.max(0, currentPageIndex - 1);
    setCurrentPageIndex(previousPageIndex);
    navigate({
      search: {
        ...searchParams,
        page: previousPageIndex + 1,
      },
    });
  };
  const onNextPage = async () => {
    const nextPageIndex = currentPageIndex + 1;
    setCurrentPageIndex(nextPageIndex);
    navigate({
      search: {
        ...searchParams,
        page: nextPageIndex + 1,
      },
    });
    if (nextPageIndex === totalPages) {
      await fetchNextPage();
    }
  };

  const onPageChange = (pageIndex: number) => setCurrentPageIndex(pageIndex);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">Videos</h1>
      </div>

      <div className="flex w-full h-full justify-between items-center mb-6">
        <form onSubmit={onSubmit} className="flex w-full grow justify-between items-center">
          <fieldset className="flex flex-wrap items-center gap-2">
            <Select name="type" value={filterType} onValueChange={onTypeChange}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User ID</SelectItem>
                <SelectItem value="org">Org ID</SelectItem>
              </SelectContent>
            </Select>
            <Input
              ref={filterIdInputRef}
              name="id"
              placeholder={`Enter ${filterType === "org" ? "org ID" : "user ID"}...`}
              value={filterId}
              onChange={(e) => {
                setFilterId(e.target.value);
              }}
              onPaste={onPaste}
              className="w-64"
              autoComplete="off"
              autoCorrect="off"
              data-1p-ignore
            />
            <Button type="submit" variant="default" size="sm" className="cursor-pointer">
              Search
            </Button>
          </fieldset>
          <div className="flex gap-2">
            <Select
              name="reference"
              value={reference || "all"}
              onValueChange={(v) => {
                setCurrentPageIndex(0);
                navigate({
                  search: {
                    ...searchParams,
                    reference: v === "all" ? undefined : (v as ReferenceMediaType),
                  },
                });
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All References" />
              </SelectTrigger>
              <SelectContent>
                {REFERENCE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              name="caller"
              value={caller || "all"}
              onValueChange={(v) => {
                setCurrentPageIndex(0);
                navigate({
                  search: {
                    ...searchParams,
                    caller: v === "all" ? undefined : v,
                  },
                });
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Callers" />
              </SelectTrigger>
              <SelectContent>
                {CALLER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex h-9 items-center space-x-2 rounded-md border border-input bg-transparent px-3 py-1 shadow-xs">
              <Checkbox
                id="twinsOnly"
                checked={twins ?? false}
                onCheckedChange={(checked) => {
                  setCurrentPageIndex(0);
                  navigate({
                    search: {
                      ...searchParams,
                      twins: checked ? true : undefined,
                    },
                  });
                }}
              />
              <label
                htmlFor="twinsOnly"
                className="text-sm font-medium leading-none cursor-pointer select-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Twins Only
              </label>
            </div>
            <div className="flex h-9 items-center space-x-2 rounded-md border border-input bg-transparent px-3 py-1 shadow-xs">
              <Checkbox
                id="excludeEmployees"
                checked={excludeEmployeesState}
                onCheckedChange={(checked) => {
                  const newChecked = checked as boolean;
                  setExcludeEmployeesState(newChecked);
                  excludeEmployeesStorage.set(newChecked);
                }}
              />
              <label
                htmlFor="excludeEmployees"
                className="text-sm font-medium leading-none cursor-pointer select-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Exclude Employees
              </label>
            </div>
            <div className="flex h-9 items-center space-x-2 rounded-md border border-input bg-transparent px-3 py-1 shadow-xs">
              <Checkbox
                id="hideProductInfo"
                checked={hideProductInfoState}
                onCheckedChange={(checked) => {
                  const newChecked = checked as boolean;
                  setHideProductInfoState(newChecked);
                  hideProductInfoStorage.set(newChecked);
                }}
              />
              <label
                htmlFor="hideProductInfo"
                className="text-sm font-medium leading-none cursor-pointer select-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Hide Product Info
              </label>
            </div>
          </div>
        </form>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      <ResponsiveGrid gap={16} maxColumns={5} itemMinWidth={200} itemMinHeight={200}>
        {(isLoading || isFetchingNextPage) &&
          Array.from({ length: 20 }).map((_, index) => (
            <Skeleton key={index} className="aspect-9/16 w-full h-full" />
          ))}
        {currentPageData?.videos.page?.map((video) => (
          <VideoCard
            key={video.id}
            video={video}
            linkTo="/videos/view/$videoId"
            linkParams={{ videoId: video.id }}
            linkSearch={{
              ...searchParams,
              page: currentPageIndex + 1,
            }}
            hideProductInfo={hideProductInfoState}
            onToggleBookmark={handleToggleBookmark}
            onFlagForDpo={handleFlagForDpo}
            isFlagForDpoPending={dpoFlagging.isFlagPending(video.id)}
            flagHoverInfo={<DpoVideoInfoCard videoId={video.id} flagging={dpoFlagging} />}
          />
        ))}
        {!isLoading && currentPageData?.videos.page?.length === 0 && (
          <div className="flex text-sm text-muted-foreground">No videos found</div>
        )}
      </ResponsiveGrid>
      <DpoSegmentFlagDialog
        videoId={dpoFlagVideoId}
        open={dpoFlagVideoId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDpoFlagVideoId(null);
          }
        }}
        flagging={dpoFlagging}
      />
      {totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end w-full mb-16">
          <div>
            <InfiniteQueryPagination
              onPreviousPage={onPreviousPage}
              onNextPage={onNextPage}
              onPageChange={onPageChange}
              currentPageIndex={currentPageIndex}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              totalPages={totalPages}
            />
          </div>
        </div>
      )}
    </div>
  );
}
